import axios from 'axios';

const TOKEN_KEY = 'tencent_admin_token';

export function getDecisionToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function setDecisionToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    /* ignore */
  }
}

const client = axios.create({ baseURL: '/api/admin' });

client.interceptors.request.use((config) => {
  const token = getDecisionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export async function decisionLogin(username, password) {
  const res = await client.post('/auth/login', { username, password });
  if (res.data?.ok && res.data.token) {
    setDecisionToken(res.data.token);
  }
  return res.data;
}

export async function decisionSession() {
  const res = await client.get('/session');
  return res.data;
}

export function decisionLogout() {
  setDecisionToken('');
}

export function getDecisionApiError(err, fallback = '操作失败') {
  return err?.response?.data?.error || fallback;
}

export async function fetchDecisionConfig(params) {
  const res = await client.get('/decision/config', { params });
  return res.data;
}

export async function saveDecisionGlobalConfig(payload, note) {
  const res = await client.put('/decision/config', { payload, note });
  return res.data;
}

export async function saveDecisionUserConfig(payload, note) {
  const res = await client.put('/decision/config/user', { payload, note });
  return res.data;
}

export async function previewDecisionConfig(productId, payload, portfolio) {
  const res = await client.post('/decision/config/preview', { productId, payload, portfolio });
  return res.data;
}

export async function fetchDecisionProduct(productId) {
  const res = await client.get(`/decision/product/${encodeURIComponent(productId)}`);
  return res.data;
}

export async function runDecisionBatch(body) {
  const res = await client.post('/decision/batch', body);
  return res.data;
}

export async function fetchDecisionPortfolio(params = {}) {
  const res = await client.get('/decision/portfolio', { params });
  return res.data;
}

export async function syncDecisionPortfolioFromCloud() {
  const res = await client.post('/decision/portfolio/sync-from-cloud');
  return res.data;
}

export async function analyzeDecisionPortfolio(body = {}) {
  const res = await client.post('/decision/portfolio/analyze', body);
  return res.data;
}

export async function saveDecisionPortfolio(portfolio) {
  const res = await client.put('/decision/portfolio', { portfolio });
  return res.data;
}

export async function fetchBondBarometerSummary(params) {
  const res = await client.get('/bond-barometer/summary', { params });
  return res.data;
}

export async function fetchDecisionProducts(institution) {
  const res = await client.get('/products/latest', {
    params: { institution, searchType: 'agent' }
  });
  return res.data;
}

export async function fetchDecisionInstitutions() {
  const res = await client.get('/institutions');
  return res.data;
}

export async function fetchMarketIndexCatalog() {
  const res = await client.get('/decision/market-index/catalog');
  return res.data;
}

export async function fetchMarketIndexCoverage() {
  const res = await client.get('/decision/market-index/coverage');
  return res.data;
}

export async function fetchDisclosureCoverage() {
  const res = await client.get('/decision/disclosure/coverage');
  return res.data;
}

export async function fetchMarketIndexHistory(indexCode, limit = 10) {
  const res = await client.get('/decision/market-index/history', {
    params: { index_code: indexCode, limit }
  });
  return res.data;
}

export async function saveManualMarketIndexRow(row) {
  const res = await client.post('/decision/market-index/manual', row);
  return res.data;
}

export async function saveManualMarketIndexRows(rows) {
  const res = await client.post('/decision/market-index/manual', { rows });
  return res.data;
}

export async function fetchMacroSummary() {
  const res = await client.get('/decision/macro/summary');
  return res.data;
}

export async function fetchMacroWatchlist() {
  const res = await client.get('/decision/macro/watchlist');
  return res.data;
}

export async function previewMacroWatchlistIndex(body) {
  const res = await client.post('/decision/macro/watchlist/preview', body);
  return res.data;
}

export async function addMacroWatchlistIndex(body) {
  const res = await client.post('/decision/macro/watchlist', body);
  return res.data;
}

export async function removeMacroWatchlistIndex(code) {
  const res = await client.delete(`/decision/macro/watchlist/${encodeURIComponent(code)}`);
  return res.data;
}

export async function resetMacroWatchlist() {
  const res = await client.post('/decision/macro/watchlist/reset');
  return res.data;
}

export async function ingestMarketIndexDaily(days = 60) {
  const res = await client.post('/decision/market-index/ingest', { days });
  return res.data;
}

export async function refreshDecisionMarketData(group, mode = 'latest', days) {
  const res = await client.post('/decision/market-data/refresh', { group, mode, days });
  return res.data;
}

export async function fetchAiUsage() {
  const res = await client.get('/ai/usage');
  return res.data;
}

export async function fetchAiLlmConfig() {
  const res = await client.get('/ai/llm-config');
  return res.data;
}

export async function saveAiLlmConfig(body) {
  const res = await client.put('/ai/llm-config', body);
  return res.data;
}

export async function testAiLlmConfig(body = {}) {
  const res = await client.post('/ai/llm-config/test', body);
  return res.data;
}

export async function pingAi() {
  const res = await client.post('/ai/ping');
  return res.data;
}

export async function postAiChat(body) {
  const res = await client.post('/ai/chat', body);
  return res.data;
}

export async function streamAiChat(body, onDelta, onDone) {
  const token = getDecisionToken();
  const res = await fetch('/api/admin/ai/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ ...body, stream: true })
  });

  if (!res.ok) {
    let errMsg = 'AI 对话失败';
    try {
      const data = await res.json();
      errMsg = data.error || errMsg;
    } catch {
      /* ignore */
    }
    throw new Error(errMsg);
  }

  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error('浏览器不支持流式响应');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    parts.forEach((block) => {
      const lines = block.split('\n').filter(Boolean);
      let event = 'message';
      let dataLine = '';
      lines.forEach((line) => {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) dataLine += line.slice(5).trim();
      });
      if (!dataLine) return;
      try {
        const payload = JSON.parse(dataLine);
        if (event === 'delta' && payload.content) onDelta?.(payload.content);
        if (event === 'done') onDone?.(payload);
        if (event === 'error') throw new Error(payload.error || 'AI 对话失败');
      } catch (err) {
        if (event === 'error') throw err;
      }
    });
  }
}
